/**
 * UPT-2095 (§11.2, démonstration) — différences STRUCTURELLES entre deux Module Spec par section du contrat
 * (`ModuleSpecDiffSection.section`, onze valeurs, `rights` comprise), pour le dépôt de démonstration seulement : le
 * serveur calcule le diff réel (`GET …/versions/{a}/diff/{b}`). Fonction pure sur deux JSON lus de façon
 * défensive ; chaque entrée est une chaîne lisible (code d'entité, `entité.champ`, code de relation, clé de page,
 * de menu, de rôle, titre de scène, nom de propriété). Sections vides omises ; deux documents identiques → `[]`.
 */
import type { ModuleSpecDiffSection, ModuleSpecDocument } from '../data/moduleSpecContracts';

type Json = Record<string, unknown>;
type Section = ModuleSpecDiffSection['section'];

const asRecord = (value: unknown): Json | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : null;
const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const asString = (value: unknown): string => (typeof value === 'string' ? value : '');
const records = (value: unknown): Json[] => asArray(value).map(asRecord).filter((r): r is Json => r !== null);

const same = (left: unknown, right: unknown): boolean => JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

/** Compare deux dictionnaires `clé → valeur` : ajouts, retraits, modifications (par égalité JSON). */
const diffMap = (before: Map<string, unknown>, after: Map<string, unknown>): Omit<ModuleSpecDiffSection, 'section'> => {
  const added: string[] = [];
  const removed: string[] = [];
  const changed: string[] = [];
  after.forEach((value, key) => {
    if (!before.has(key)) added.push(key);
    else if (!same(before.get(key), value)) changed.push(key);
  });
  before.forEach((_value, key) => {
    if (!after.has(key)) removed.push(key);
  });
  return { added: added.sort(), removed: removed.sort(), changed: changed.sort() };
};

const keyed = (items: Json[], keyOf: (item: Json, index: number) => string): Map<string, unknown> =>
  new Map(items.map((item, index) => [keyOf(item, index) || `#${index}`, item]));

const propsOf = (record: Json | null): Map<string, unknown> => new Map(Object.entries(record ?? {}));

const entityWithoutFields = (entity: Json): Json => {
  const copy: Json = { ...entity };
  delete copy.fields;
  delete copy.workflow;
  return copy;
};

const fieldsOf = (entities: Json): Map<string, unknown> => {
  const map = new Map<string, unknown>();
  Object.entries(entities).forEach(([code, raw]) => {
    const entity = asRecord(raw);
    if (!entity) return;
    records(entity.fields).forEach((field, index) => {
      map.set(`${code}.${asString(field.name) || `#${index}`}`, field);
    });
  });
  return map;
};

const workflowsOf = (entities: Json): Map<string, unknown> => {
  const map = new Map<string, unknown>();
  Object.entries(entities).forEach(([code, raw]) => {
    const entity = asRecord(raw);
    if (entity && entity.workflow !== undefined) map.set(code, entity.workflow);
  });
  return map;
};

const KNOWN_ROOT: readonly string[] = [
  'module', 'entities', 'relations', 'pages', 'navigation', 'permissions', 'testPlan', 'rights',
];

export function moduleSpecLocalDiff(from: ModuleSpecDocument | unknown, to: ModuleSpecDocument | unknown): ModuleSpecDiffSection[] {
  const before = asRecord(from) ?? {};
  const after = asRecord(to) ?? {};
  const beforeEntities = asRecord(before.entities) ?? {};
  const afterEntities = asRecord(after.entities) ?? {};
  const entityMap = (entities: Json): Map<string, unknown> =>
    new Map(Object.entries(entities).map(([code, raw]) => [code, asRecord(raw) ? entityWithoutFields(asRecord(raw) as Json) : raw]));
  const navMenus = (root: Json): Map<string, unknown> =>
    keyed(records(asRecord(root.navigation)?.menus), (menu) => asString(menu.key));
  const roles = (root: Json): Map<string, unknown> =>
    keyed(records(asRecord(root.permissions)?.roles), (role) => asString(role.key));
  const scenes = (root: Json): Map<string, unknown> =>
    keyed(records(asRecord(root.testPlan)?.scenes), (scene) => asString(scene.title));
  const others = (root: Json): Map<string, unknown> =>
    new Map(Object.entries(root).filter(([key]) => !KNOWN_ROOT.includes(key)));

  const sections: { section: Section; diff: Omit<ModuleSpecDiffSection, 'section'> }[] = [
    { section: 'module', diff: diffMap(propsOf(asRecord(before.module)), propsOf(asRecord(after.module))) },
    { section: 'entities', diff: diffMap(entityMap(beforeEntities), entityMap(afterEntities)) },
    { section: 'fields', diff: diffMap(fieldsOf(beforeEntities), fieldsOf(afterEntities)) },
    { section: 'relations', diff: diffMap(keyed(records(before.relations), (r) => asString(r.code)), keyed(records(after.relations), (r) => asString(r.code))) },
    { section: 'pages', diff: diffMap(keyed(records(before.pages), (p) => asString(p.key)), keyed(records(after.pages), (p) => asString(p.key))) },
    { section: 'navigation', diff: diffMap(navMenus(before), navMenus(after)) },
    { section: 'permissions', diff: diffMap(roles(before), roles(after)) },
    { section: 'workflow', diff: diffMap(workflowsOf(beforeEntities), workflowsOf(afterEntities)) },
    { section: 'testPlan', diff: diffMap(scenes(before), scenes(after)) },
    { section: 'rights', diff: diffMap(propsOf(asRecord(before.rights)), propsOf(asRecord(after.rights))) },
    { section: 'other', diff: diffMap(others(before), others(after)) },
  ];
  return sections
    .filter(({ diff }) => diff.added.length + diff.removed.length + diff.changed.length > 0)
    .map(({ section, diff }) => ({ section, ...diff }));
}
